import { ArrowRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { AuditionVideo } from '@/lib/audition/intro-video'
import { MINUTES_TO_APPLY } from '@/lib/audition/steps'
import { StepVideo } from './step-video'

/**
 * The screen ahead of step 1. It is a phase, not a wizard step: it has no
 * fields, no step number and no place on the stage rail, because it has
 * exactly one job, which is to get the video watched before anyone starts
 * typing.
 *
 * Order is deliberate — the instruction, then the player, then the welcome
 * copy under it. Someone who came here to apply should hit the video before
 * they hit any prose they might skim past.
 */
export function WelcomeScreen({
  video,
  onStart,
}: {
  video: AuditionVideo
  onStart: () => void
}) {
  return (
    <section className="mx-auto flex w-full max-w-3xl flex-col items-center gap-6 text-center">
      <h1 className="font-semibold text-3xl tracking-tight sm:text-4xl">
        Watch this video
      </h1>

      <StepVideo video={video} className="w-full" rememberSeen={false} />

      <h2 className="font-semibold text-xl tracking-tight sm:text-2xl">
        Welcome to your audition
      </h2>

      <p className="max-w-xl text-muted-foreground leading-relaxed">
        We don't sell coaching programs or mock interviews, we audition for
        them. This is your audition, so we can see if you're the right fit for
        us. It'll take about {MINUTES_TO_APPLY} minutes.
      </p>

      {/* The pro tip used to claim that leaving mid-flow lost your answers.
          It never did: the moment you submit the contact step we create the
          row and keep a resume link. Claiming otherwise was a lie that a
          returning applicant would catch us in, so the urgency now comes
          from the honest reason — these answers are better written in one
          sitting. */}
      <p className="max-w-xl text-muted-foreground leading-relaxed">
        <span className="font-medium text-foreground">Pro tip:</span> give
        yourself the full {MINUTES_TO_APPLY} minutes and answer everything in
        one go. Your progress is saved from the moment you enter your email, so
        you can come back to it — but these answers read far better written in a
        single sitting. <em>Ready?</em> Let's do this.
      </p>

      <Button type="button" onClick={onStart} className="mt-2">
        Get started
        <ArrowRight aria-hidden="true" className="size-4" />
      </Button>
    </section>
  )
}
